import * as React from 'react'
import { useCallback, useRef, useState } from 'react'

import { UNIFIED_SEARCH_ENABLE } from '@disco/constants/FeatureTypes'
import {
  clearSearch,
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import { getConstanceSetting } from '@disco/state-management/selectors/user'

import type { FilterState } from 'types/filter-page'
import type { SearchType as SearchMode } from 'types/search-page'

import { Button, Flex, Popover, SearchTextField } from 'components/ds'
import {
  FilterActionAddButton,
  FilterActionClearAllButton,
  SelectedFilterBadges,
} from 'components/search/search-filters'

import { useDispatch, useSelector } from 'hooks/use-redux'

import AiSearchOverlay from 'views/pages/search/search-bar/ai-search-overlay'
import AiSearchToggle from 'views/pages/search/search-bar/ai-search-toggle'
import { useAiSearchToggle } from 'views/pages/search/search-bar/use-ai-search-toggle'
import { useFiltersDefinition } from 'views/pages/search/utils'
import { usePromptSearchEnabled } from 'views/pages/search/utils/use-prompt-search-enabled'
import { useSimSearchEnabled } from 'views/pages/search/utils/use-sim-search-enabled'

import styles from './index.module.css'

/**
 * Prototype topbar search: the unified-search-page look (single input + the
 * generic "+ Filter" badges) wired to the legacy `search` slice so results
 * still render in the explorer/Browse pane. Mirrors the unified search bar
 * (UNIFIED_SEARCH_ENABLE): keyword by default, a URL auto-routes to similarity,
 * and the AI toggle switches the query to Prompt (LLM) search.
 */

// Badges shown inline before the rest collapse into "+N more".
const MAX_INLINE_FILTERS = 2

// A filter the user hasn't given a value yet (e.g. a "+ Filter → BPM" shell)
// carries no search intent, so it's dropped before the state reaches redux.
const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim().length === 0) ||
  (Array.isArray(value) && value.length === 0)

// Stable identity of the filters that actually affect a search — order and the
// empty shells excluded — used to skip re-running a search that hasn't changed.
const activeFilterKey = (states: FilterState[]): string =>
  JSON.stringify(
    states
      .filter(state => !isEmptyValue(state.value))
      .map(state => [state.definition.field, state.operator ?? null, state.value]),
  )

const UnifiedSearch = (): React.ReactElement => {
  const dispatch = useDispatch()
  const searchTerm = useSelector(state => state.search.searchTerm)
  const searchMode = useSelector(state => state.search.searchMode) ?? 'keyword'
  const definitions = useFiltersDefinition()
  const [filters, setFilters] = useState<FilterState[]>([])

  const simSearchEnabled = useSimSearchEnabled()
  const promptSearchEnabled = usePromptSearchEnabled()
  const unifiedSearchEnabled = Boolean(
    useSelector(state => getConstanceSetting(state, UNIFIED_SEARCH_ENABLE)),
  )
  // The AI toggle + overlay are the unified search bar's Prompt affordance —
  // shown only when unified search is on and the business can run prompt (LLM)
  // search. aiMode ('off' | 'active' | 'overlay') is the shared overlay state.
  const showAiToggle = unifiedSearchEnabled && promptSearchEnabled
  const { aiMode, aiPrompt, setAiMode, setAiPrompt, clearPrompt, rootRef } =
    useAiSearchToggle()

  const setSearchMode = useCallback(
    (mode: SearchMode) => {
      dispatch(updateSearchParams({ searchMode: mode }))
      // Re-run under the new mode if there's anything to search.
      if (searchTerm?.trim() || filters.length > 0) {
        dispatch(getSearchResults())
      }
    },
    [dispatch, searchTerm, filters],
  )

  // Expand the overlay, pre-filling it with the current query so the user can
  // refine an existing search as a prompt.
  const openOverlay = useCallback(() => {
    if (searchTerm?.trim()) setAiPrompt(searchTerm)
    setAiMode('overlay')
  }, [searchTerm, setAiPrompt, setAiMode])

  // Turning the toggle off reverts to keyword search for the current query.
  const handleAiDeactivate = useCallback(() => {
    setAiMode('off')
    clearPrompt()
    setSearchMode('keyword')
  }, [setAiMode, clearPrompt, setSearchMode])

  // Submitting the overlay runs the prompt as an LLM search in the Browse pane,
  // then collapses the overlay while leaving the toggle active.
  const handleAiSubmit = useCallback(() => {
    const prompt = aiPrompt.trim()
    if (!prompt) return
    dispatch(
      updateSearchParams({
        searchTerm: prompt,
        searchMode: 'prompt',
        searchOpen: true,
      }),
    )
    dispatch(getSearchResults())
    setAiMode('active')
    clearPrompt()
  }, [aiPrompt, dispatch, setAiMode, clearPrompt])

  // Re-expand the overlay when the field is refocused while AI stays active.
  const handleFocus = useCallback(() => {
    if (aiMode === 'active') openOverlay()
  }, [aiMode, openOverlay])

  // Active-filter identity of the last search we actually ran. Adding a filter
  // shell (e.g. "+ Filter → BPM", before a value is entered) doesn't change it
  // — activeFilterKey drops empty-value filters — so gating on this key stops
  // the empty add from firing a premature search.
  const lastSearchKeyRef = useRef<string>(activeFilterKey([]))

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(updateSearchParams({ searchTerm: e.target.value }))
    },
    [dispatch],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Tab from the collapsed field opens AI search (matches the placeholder).
      if (showAiToggle && e.key === 'Tab' && aiMode === 'off') {
        e.preventDefault()
        openOverlay()
        return
      }
      if (e.key !== 'Enter') return
      dispatch(updateSearchParams({ searchOpen: true }))
      dispatch(getSearchResults())
    },
    [dispatch, showAiToggle, aiMode, openOverlay],
  )

  const handleFiltersChange = useCallback(
    (next: FilterState[]) => {
      // Always reflect the change in the badges/state, even for an empty
      // filter shell — the value dropdown needs its badge to render.
      setFilters(next)

      const key = activeFilterKey(next)
      // Only touch the search slice / re-run when the effective filters
      // changed. An empty filter shell keys identically, so we skip it and
      // wait for a value (or a removal) to actually run the search.
      if (key === lastSearchKeyRef.current) return
      lastSearchKeyRef.current = key

      // Store only the value-bearing filters; getSearchResults builds the v2
      // query straight from these (see buildGlobalV2SearchQuery's filterState).
      const activeFilters = next.filter(filter => !isEmptyValue(filter.value))

      // Removing the last badge with no text query leaves nothing to search:
      // getSearchResults' hasFilters gate would bail early and leave the stale
      // results on screen (unless a track-only filter had forced type=TRACK).
      // Reset to the default browse panel, exactly like "Clear all".
      if (activeFilters.length === 0 && !searchTerm?.trim()) {
        dispatch(clearSearch(false))
        return
      }

      dispatch(updateSearchParams({ filterState: activeFilters, searchOpen: true }))
      dispatch(getSearchResults())
    },
    [dispatch, searchTerm],
  )

  const handleClearAll = useCallback(() => {
    setFilters([])
    // Back to the empty-filter baseline so the next empty shell added after a
    // reset is still recognised as "no change" and doesn't fire a search.
    lastSearchKeyRef.current = activeFilterKey([])
    // Resets query + filters (filterState included) and returns to the default
    // browse panel.
    dispatch(clearSearch(false))
  }, [dispatch])

  // The topbar can't grow past its fixed 46px, so only the first few badges
  // stay inline; the rest collapse into a "+N more" popover.
  const visibleFilters = filters.slice(0, MAX_INLINE_FILTERS)
  const overflowFilters = filters.slice(MAX_INLINE_FILTERS)

  // SelectedFilterBadges edits the array it's handed, so each half has to be
  // spliced back into the full list before it reaches redux — otherwise
  // editing a visible badge would drop every overflowed one (and vice versa).
  const handleVisibleChange = useCallback(
    (next: FilterState[]) => handleFiltersChange([...next, ...overflowFilters]),
    [handleFiltersChange, overflowFilters],
  )

  const handleOverflowChange = useCallback(
    (next: FilterState[]) => handleFiltersChange([...visibleFilters, ...next]),
    [handleFiltersChange, visibleFilters],
  )

  // Mirror the /search page's Reset: surface it whenever there's anything to
  // clear — a query OR any filter — not just when filters exist.
  const hasClearableState = Boolean(searchTerm?.trim()) || filters.length > 0

  return (
    <Flex direction="row" align="center" gap="2" className={styles.wrapper}>
      <div ref={rootRef} className={styles.searchFieldWrapper}>
        <SearchTextField
          value={searchTerm ?? ''}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={
            showAiToggle
              ? 'Search Keywords or Press Tab for AI Search'
              : simSearchEnabled
              ? 'Search keywords or paste a link'
              : 'Search keywords'
          }
          iconPosition="left"
          nodeRight={
            showAiToggle ? (
              <AiSearchToggle
                active={aiMode !== 'off'}
                onActivate={openOverlay}
                onDeactivate={handleAiDeactivate}
              />
            ) : undefined
          }
          className={styles.input}
        />
        {showAiToggle && aiMode === 'overlay' && (
          <AiSearchOverlay
            value={aiPrompt}
            onChange={setAiPrompt}
            onClear={clearPrompt}
            onSubmit={handleAiSubmit}
          />
        )}
      </div>
      <div className={styles.addFilter}>
        <FilterActionAddButton
          value={filters}
          definitions={definitions}
          onChange={handleFiltersChange}
        />
      </div>
      <div className={styles.badges}>
        <SelectedFilterBadges
          value={visibleFilters}
          definitions={definitions}
          seedDefaultState={false}
          onChange={handleVisibleChange}
        />
        {overflowFilters.length > 0 && (
          <Popover.Root>
            <Popover.Trigger>
              <Button variant="soft" size="1" className={styles.moreButton}>
                +{overflowFilters.length} more
              </Button>
            </Popover.Trigger>
            <Popover.Content size="1" className={styles.morePopover}>
              <Flex direction="column" align="start" gap="2">
                <SelectedFilterBadges
                  value={overflowFilters}
                  definitions={definitions}
                  seedDefaultState={false}
                  onChange={handleOverflowChange}
                />
              </Flex>
            </Popover.Content>
          </Popover.Root>
        )}
        {hasClearableState && (
          <FilterActionClearAllButton
            definitions={definitions}
            seedDefaultState={false}
            onChange={handleClearAll}
          />
        )}
      </div>
    </Flex>
  )
}

export default UnifiedSearch
